// WMTS / OGC GetCapabilities parsing and tile-URL plumbing. Lives next to its only consumer:
// nothing on the map reads any of this, and it used to sit under MapBoxViewer/ where it looked
// like map code. No JSX, so no .tsx.

export type WMTSValidationResult = {
  isValid: boolean
  status?: number
  triedUrls: string[]
}

export interface TileMatrixSet {
  identifier: string
  supportedCRS: string
  tileMatrices?: {
    identifier: string
    scaleDenominator: number
    topLeftCorner?: string
    tileWidth: number
    tileHeight: number
    matrixWidth: number
    matrixHeight: number
  }[]
}

export interface WMTSDimension {
  identifier: string
  title?: string
  abstract?: string
  uom?: string // Units of measure (e.g., ISO8601 for time)
  unitSymbol?: string
  default?: string
  current?: boolean
  values: string[]
}

export interface WMTSLayer {
  identifier?: string
  title?: string
  abstract?: string
  formats?: string[]
  boundingBox?: {
    west: number
    south: number
    east: number
    north: number
  }
  tileMatrixSetLinks?: string[]
  resourceUrls?: {
    format: string
    resourceType: string
    template: string
  }[]
  styles?: {
    identifier?: string
    isDefault: boolean
  }[]
  dimensions?: WMTSDimension[]
}

export interface WMTSCapabilities {
  type: 'WMTS'
  version?: string
  service?: {
    title?: string
    abstract?: string
    name?: string
    keywords?: string[]
  }
  layers?: WMTSLayer[]
  tileMatrixSets?: TileMatrixSet[]
}

const defaultWMTSCapabilities: WMTSCapabilities = {
  type: 'WMTS',
  service: { title: 'Unknown Service' },
  version: 'Unknown',
  layers: [],
  tileMatrixSets: [],
} as WMTSCapabilities

export const ACCEPTABLE_WMTS_TILE_STATUSES = [204, 401, 403, 404]

// Service type detection function - only WMTS
export async function detectOGCServiceType(
  url: string,
): Promise<'WMTS' | 'Unknown'> {
  try {
    const testUrl = new URL(url)
    const baseUrl = new URL(
      `${testUrl.protocol}//${testUrl.host}${testUrl.pathname}`,
    )

    // Preserve any existing query parameters (like jwt tokens)
    testUrl.searchParams.forEach((value, key) => {
      if (key !== 'service' && key !== 'request' && key !== 'version') {
        baseUrl.searchParams.set(key, value)
      }
    })

    baseUrl.searchParams.set('request', 'GetCapabilities')
    baseUrl.searchParams.set('service', 'WMTS')
    baseUrl.searchParams.set('version', '1.0.0')

    const response = await fetch(baseUrl.toString())
    if (response.ok) {
      const text = await response.text()
      const xmlDoc = new DOMParser().parseFromString(text, 'text/xml')
      const root = xmlDoc.documentElement

      // Check if it's a valid WMTS response
      if (
        root.nodeName === 'Capabilities' &&
        (root.namespaceURI?.includes('wmts') ||
          root.getAttribute('xmlns')?.includes('wmts'))
      ) {
        return 'WMTS'
      }
    }

    return 'Unknown'
  } catch (error) {
    console.error('Error detecting OGC service type:', error)
    return 'Unknown'
  }
}

// Main function to get OGC capabilities - only WMTS
export async function getOGCCapabilities(
  url: string,
): Promise<WMTSCapabilities | null> {
  const serviceType = await detectOGCServiceType(url)

  if (serviceType === 'WMTS') {
    return fetchCapabilities(url, serviceType)
  }

  throw new Error(
    `Unsupported service type: ${serviceType}. Only WMTS is supported.`,
  )
}

async function fetchCapabilities(
  baseUrl: string,
  service: 'WMTS',
): Promise<WMTSCapabilities> {
  try {
    // Create a new URL object to manipulate the base URL
    const url = new URL(baseUrl)
    const baseUrlOnly = new URL(`${url.protocol}//${url.host}${url.pathname}`)
    baseUrlOnly.searchParams.set('service', service)
    baseUrlOnly.searchParams.set('request', 'GetCapabilities')
    baseUrlOnly.searchParams.set('version', '1.0.0')

    // Preserve important query parameters
    if (url.searchParams.has('jwttoken')) {
      baseUrlOnly.searchParams.set(
        'jwttoken',
        url.searchParams.get('jwttoken') || '',
      )
    }
    if (url.searchParams.has('jwt_token')) {
      baseUrlOnly.searchParams.set(
        'jwt_token',
        url.searchParams.get('jwt_token') || '',
      )
    }

    const response = await fetch(baseUrlOnly.toString())
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const xmlText = await response.text()
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml')

    return parseWMTSCapabilities(xmlDoc)
  } catch (error) {
    console.error('Error fetching WMTS capabilities:', error)
    return defaultWMTSCapabilities
  }
}

// Update the parseWMTSCapabilities function to fully support the schema
function parseWMTSCapabilities(xmlDoc: Document): WMTSCapabilities {
  // Define namespaces based on the schema
  const ns = {
    ows: 'http://www.opengis.net/ows/1.1',
    wmts: 'http://www.opengis.net/wmts/1.0',
    xlink: 'http://www.w3.org/1999/xlink',
  }

  // Helper function to get elements with namespace awareness
  function getElementsWithNS(
    parent: Element,
    namespace: string,
    localName: string,
  ): Element[] {
    // Try namespace-aware query first
    let elements = Array.from(
      parent.getElementsByTagNameNS(namespace, localName),
    )

    // Fallback to non-namespaced query if needed
    if (elements.length === 0) {
      elements = Array.from(
        parent.getElementsByTagName(
          `${namespace.split('/').pop()}:${localName}`,
        ),
      )
      if (elements.length === 0) {
        elements = Array.from(parent.getElementsByTagName(localName))
      }
    }

    return elements
  }

  // Helper function to get a single element
  function getElementWithNS(
    parent: Element,
    namespace: string,
    localName: string,
  ): Element | null {
    const elements = getElementsWithNS(parent, namespace, localName)
    return elements.length > 0 ? elements[0] : null
  }

  // Helper for text content
  function getTextContent(
    parent: Element,
    namespace: string,
    localName: string,
  ): string | undefined {
    const element = getElementWithNS(parent, namespace, localName)
    return element?.textContent?.trim() || undefined
  }

  // Helper for attribute values
  function getAttributeValue(
    element: Element,
    attributeName: string,
  ): string | undefined {
    return element?.getAttribute(attributeName) || undefined
  }

  const root = xmlDoc.documentElement
  const version = root.getAttribute('version') || '1.0.0'

  // Parse Service Identification
  const serviceIdEl = getElementWithNS(root, ns.ows, 'ServiceIdentification')
  const service = {
    title: serviceIdEl
      ? getTextContent(serviceIdEl, ns.ows, 'Title')
      : undefined,
    abstract: serviceIdEl
      ? getTextContent(serviceIdEl, ns.ows, 'Abstract')
      : undefined,
    name: serviceIdEl
      ? getTextContent(serviceIdEl, ns.ows, 'ServiceType')
      : undefined,
    keywords: [],
  }

  // Parse keywords if present
  const keywordListEl = serviceIdEl
    ? getElementWithNS(serviceIdEl, ns.ows, 'Keywords')
    : null
  if (keywordListEl) {
    const keywords = getElementsWithNS(keywordListEl, ns.ows, 'Keyword')
      .map(k => k.textContent?.trim())
      .filter(Boolean) as string[]
    service.keywords = keywords
  }

  // Parse Contents element
  const contentsEl = getElementWithNS(root, ns.wmts, 'Contents')
  const layers: WMTSLayer[] = []
  const tileMatrixSets: TileMatrixSet[] = []

  if (contentsEl) {
    // Parse Layers
    const layerElements = getElementsWithNS(contentsEl, ns.wmts, 'Layer')
    for (const layerEl of layerElements) {
      // Get layer identifier from ows:Identifier
      const identifier = getTextContent(layerEl, ns.ows, 'Identifier') || ''
      const title = getTextContent(layerEl, ns.ows, 'Title')
      const abstract = getTextContent(layerEl, ns.ows, 'Abstract')

      // Get formats
      const formats = getElementsWithNS(layerEl, ns.wmts, 'Format')
        .map(f => f.textContent?.trim() || '')
        .filter(Boolean)

      // Get WGS84 bounding box
      let boundingBox: WMTSLayer['boundingBox'] | undefined
      const wgs84BBoxEl = getElementWithNS(layerEl, ns.ows, 'WGS84BoundingBox')
      if (wgs84BBoxEl) {
        const lower = getTextContent(wgs84BBoxEl, ns.ows, 'LowerCorner')
          ?.split(' ')
          .map(Number)
        const upper = getTextContent(wgs84BBoxEl, ns.ows, 'UpperCorner')
          ?.split(' ')
          .map(Number)
        if (lower && upper && lower.length === 2 && upper.length === 2) {
          boundingBox = {
            west: lower[0],
            south: lower[1],
            east: upper[0],
            north: upper[1],
          }
        }
      }

      // Get TileMatrixSetLinks
      const tileMatrixSetLinks = getElementsWithNS(
        layerEl,
        ns.wmts,
        'TileMatrixSetLink',
      )
        .map(linkEl => {
          return getTextContent(linkEl, ns.wmts, 'TileMatrixSet')
        })
        .filter(Boolean) as string[]

      // Get ResourceURLs (with proper attribute handling)
      const resourceUrls = getElementsWithNS(layerEl, ns.wmts, 'ResourceURL')
        .map(resEl => ({
          format: getAttributeValue(resEl, 'format') || '',
          resourceType: getAttributeValue(resEl, 'resourceType') || '',
          template: getAttributeValue(resEl, 'template') || '',
        }))
        .filter(ru => ru.template && ru.format) // Filter out incomplete resource URLs

      // Get Style information
      const styles = getElementsWithNS(layerEl, ns.wmts, 'Style')
        .map(styleEl => {
          const styleId = getTextContent(styleEl, ns.ows, 'Identifier')
          const isDefault = styleEl.getAttribute('isDefault') === 'true'
          return { identifier: styleId, isDefault }
        })
        .filter(s => s.identifier)

      // Get Dimensions
      const dimensions: WMTSDimension[] = []
      const dimensionElements = getElementsWithNS(layerEl, ns.wmts, 'Dimension')
      for (const dimEl of dimensionElements) {
        const identifier = getTextContent(dimEl, ns.ows, 'Identifier') || ''
        const title = getTextContent(dimEl, ns.ows, 'Title')
        const abstract = getTextContent(dimEl, ns.ows, 'Abstract')
        const uom = getTextContent(dimEl, ns.ows, 'UOM')
        const unitSymbol = getTextContent(dimEl, ns.wmts, 'UnitSymbol')
        const defaultValue = getTextContent(dimEl, ns.wmts, 'Default')
        const current = getTextContent(dimEl, ns.wmts, 'Current') === 'true'

        // Get all Value elements
        const values = getElementsWithNS(dimEl, ns.wmts, 'Value')
          .map(v => v.textContent?.trim() || '')
          .filter(Boolean)

        if (identifier) {
          dimensions.push({
            identifier,
            title,
            abstract,
            uom,
            unitSymbol,
            default: defaultValue,
            current,
            values,
          })
        }
      }

      layers.push({
        identifier,
        title,
        abstract,
        formats,
        boundingBox,
        tileMatrixSetLinks,
        resourceUrls,
        styles,
        dimensions,
      })
    }

    // Parse TileMatrixSets
    const tileMatrixSetElements = getElementsWithNS(
      contentsEl,
      ns.wmts,
      'TileMatrixSet',
    )
    for (const tmsEl of tileMatrixSetElements) {
      const identifier = getTextContent(tmsEl, ns.ows, 'Identifier') || ''
      const supportedCRS = getTextContent(tmsEl, ns.ows, 'SupportedCRS') || ''

      // Parse individual TileMatrix elements if needed
      const tileMatrices = getElementsWithNS(tmsEl, ns.wmts, 'TileMatrix')
        .map(tmEl => ({
          identifier: getTextContent(tmEl, ns.ows, 'Identifier') || '',
          scaleDenominator: parseFloat(
            getTextContent(tmEl, ns.wmts, 'ScaleDenominator') || '0',
          ),
          topLeftCorner: getTextContent(tmEl, ns.wmts, 'TopLeftCorner'),
          tileWidth: parseInt(
            getTextContent(tmEl, ns.wmts, 'TileWidth') || '0',
            10,
          ),
          tileHeight: parseInt(
            getTextContent(tmEl, ns.wmts, 'TileHeight') || '0',
            10,
          ),
          matrixWidth: parseInt(
            getTextContent(tmEl, ns.wmts, 'MatrixWidth') || '0',
            10,
          ),
          matrixHeight: parseInt(
            getTextContent(tmEl, ns.wmts, 'MatrixHeight') || '0',
            10,
          ),
        }))
        .filter(tm => tm.identifier)

      tileMatrixSets.push({
        identifier,
        supportedCRS,
        tileMatrices,
      })
    }
  }

  return {
    type: 'WMTS',
    version,
    service,
    layers,
    tileMatrixSets,
  }
}

export function standardizeWMTSTileUrl(
  urlTemplate: string,
  style?: string,
  tileMatrixSet?: string,
  format?: string,
  dimensions?: { [dimensionId: string]: string },
): string {
  try {
    let standardizedUrl = urlTemplate

    // Define all possible WMTS template parameters and their standard equivalents
    const coordinateParameterMappings = {
      '{TileMatrix}': '{z}',
      '{TileRow}': '{y}',
      '{TileCol}': '{x}',
      '{TILEMATRIX}': '{z}',
      '{TILEROW}': '{y}',
      '{TILECOL}': '{x}',
      '{tilematrix}': '{z}',
      '{tilerow}': '{y}',
      '{tilecol}': '{x}',
      '{level}': '{z}',
      '{row}': '{y}',
      '{col}': '{x}',
    }

    // Apply coordinate parameter mappings first
    Object.entries(coordinateParameterMappings).forEach(
      ([wmtsParam, standardParam]) => {
        standardizedUrl = standardizedUrl.replace(
          new RegExp(wmtsParam.replace(/[{}]/g, '\\$&'), 'gi'),
          standardParam,
        )
      },
    )

    // Replace service-specific parameters
    if (style) {
      const stylePatterns = ['{Style}', '{STYLE}', '{style}']
      stylePatterns.forEach(pattern => {
        standardizedUrl = standardizedUrl.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
          style,
        )
      })
    }

    if (tileMatrixSet) {
      const tmsPatterns = [
        '{TileMatrixSet}',
        '{TILEMATRIXSET}',
        '{tilematrixset}',
      ]
      tmsPatterns.forEach(pattern => {
        standardizedUrl = standardizedUrl.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
          tileMatrixSet,
        )
      })
    }

    if (format) {
      const formatPatterns = ['{Format}', '{FORMAT}', '{format}']
      formatPatterns.forEach(pattern => {
        standardizedUrl = standardizedUrl.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
          format,
        )
      })
    }

    // Helper function to parse time range values
    const parseTimeValue = (value: string) => {
      // Handle ISO 8601 time ranges: start/end/period or start/end or single date
      if (value.includes('/')) {
        const parts = value.split('/')
        return {
          start: parts[0],
          end: parts.length > 1 ? parts[1] : parts[0],
          period: parts.length > 2 ? parts[2] : undefined,
          isRange: true,
        }
      }
      return {
        start: value,
        end: value,
        period: undefined,
        isRange: false,
      }
    }

    // Replace dimension parameters
    if (dimensions) {
      Object.entries(dimensions).forEach(([dimensionId, value]) => {
        if (value) {
          // Special handling for Time dimension
          if (dimensionId.toLowerCase() === 'time') {
            const timeInfo = parseTimeValue(value)

            // Replace TimeStart and TimeEnd patterns
            const timeStartPatterns = [
              '{TimeStart}',
              '{TIMESTART}',
              '{timestart}',
            ]
            const timeEndPatterns = ['{TimeEnd}', '{TIMEEND}', '{timeend}']

            timeStartPatterns.forEach(pattern => {
              standardizedUrl = standardizedUrl.replace(
                new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
                timeInfo.start,
              )
            })

            timeEndPatterns.forEach(pattern => {
              standardizedUrl = standardizedUrl.replace(
                new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
                timeInfo.end,
              )
            })

            // Replace general Time patterns with the full value or start date
            const timePatterns = ['{Time}', '{TIME}', '{time}']
            timePatterns.forEach(pattern => {
              standardizedUrl = standardizedUrl.replace(
                new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
                timeInfo.isRange ? timeInfo.start : value,
              )
            })
          }

          // Handle all other dimensions normally
          const dimensionPatterns = [
            `{${dimensionId}}`,
            `{${dimensionId.toUpperCase()}}`,
            `{${dimensionId.toLowerCase()}}`,
            // Handle common dimension aliases (excluding time which we handled above)
            ...(dimensionId.toLowerCase() === 'elevation'
              ? ['{Elevation}', '{ELEVATION}', '{elevation}']
              : []),
            ...(dimensionId.toLowerCase() === 'band'
              ? ['{Band}', '{BAND}', '{band}']
              : []),
          ]

          dimensionPatterns.forEach(pattern => {
            standardizedUrl = standardizedUrl.replace(
              new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'gi'),
              value,
            )
          })
        }
      })
    }

    // Handle any remaining template parameters with defaults
    const defaultReplacements: [RegExp, string][] = [
      [/{Style}/gi, 'default'],
      [/{STYLE}/gi, 'default'],
      [/{style}/gi, 'default'],
      [/{TileMatrixSet}/gi, 'WEBMERCATOR'],
      [/{TILEMATRIXSET}/gi, 'WEBMERCATOR'],
      [/{tilematrixset}/gi, 'WEBMERCATOR'],
      [/{Time}/gi, 'default'],
      [/{TIME}/gi, 'default'],
      [/{time}/gi, 'default'],
      [/{TimeStart}/gi, 'default'],
      [/{TIMESTART}/gi, 'default'],
      [/{timestart}/gi, 'default'],
      [/{TimeEnd}/gi, 'default'],
      [/{TIMEEND}/gi, 'default'],
      [/{timeend}/gi, 'default'],
      [/{Elevation}/gi, 'default'],
      [/{ELEVATION}/gi, 'default'],
      [/{elevation}/gi, 'default'],
      [/{Band}/gi, 'default'],
      [/{BAND}/gi, 'default'],
      [/{band}/gi, 'default'],
      [/{BBOX}/gi, 'default'],
      [/{bbox}/gi, 'default'],
    ]

    defaultReplacements.forEach(([pattern, replacement]) => {
      standardizedUrl = standardizedUrl.replace(pattern, replacement)
    })

    return standardizedUrl
  } catch (error) {
    console.error('Error standardizing WMTS tile URL:', error)
    return urlTemplate
  }
}

const clamp = (val: number, min: number, max: number) =>
  Math.max(min, Math.min(max, val))

function lngLatToTileXY(
  lon: number,
  lat: number,
  z: number,
  matrixWidth: number,
  matrixHeight: number,
): { x: number; y: number } {
  const n = Math.pow(2, z)
  let x = Math.floor(((lon + 180) / 360) * n)
  const latRad = (lat * Math.PI) / 180
  let y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  )
  x = clamp(x, 0, matrixWidth - 1)
  y = clamp(y, 0, matrixHeight - 1)
  return { x, y }
}

export async function validateWMTSLink(
  resLink: string,
  wmtsCapabilities: WMTSCapabilities,
  selectedLayerIndex: number,
  selectedTileMatrixSet: string,
  maxTries = 3,
): Promise<WMTSValidationResult> {
  if (!resLink) return { isValid: false, triedUrls: [] }

  const layer = wmtsCapabilities?.layers?.[selectedLayerIndex]
  const tmsId = selectedTileMatrixSet || layer?.tileMatrixSetLinks?.[0]
  const tms = wmtsCapabilities?.tileMatrixSets?.find(
    t => t.identifier === tmsId,
  )
  const triedUrls: string[] = []

  if (!tms || !tms.tileMatrices?.length) return { isValid: false, triedUrls }

  // Pick a zoom level in the middle
  const midIdx = Math.floor(tms.tileMatrices.length / 2)
  const matrix = tms.tileMatrices[midIdx]
  const z = Number(matrix.identifier)

  // Try to use the center of the bounding box if available
  let coordsToTry: { x: number; y: number }[] = []
  if (layer?.boundingBox) {
    const centerLon = (layer.boundingBox.west + layer.boundingBox.east) / 2
    const centerLat = (layer.boundingBox.south + layer.boundingBox.north) / 2
    const center = lngLatToTileXY(
      centerLon,
      centerLat,
      z,
      matrix.matrixWidth,
      matrix.matrixHeight,
    )
    const nw = lngLatToTileXY(
      layer.boundingBox.west,
      layer.boundingBox.north,
      z,
      matrix.matrixWidth,
      matrix.matrixHeight,
    )
    const se = lngLatToTileXY(
      layer.boundingBox.east,
      layer.boundingBox.south,
      z,
      matrix.matrixWidth,
      matrix.matrixHeight,
    )
    coordsToTry = [center, nw, se]
  } else {
    // Fallback: try center and corners of the matrix
    coordsToTry = [
      {
        x: Math.floor(matrix.matrixWidth / 2),
        y: Math.floor(matrix.matrixHeight / 2),
      },
      { x: 0, y: 0 },
      { x: matrix.matrixWidth - 1, y: matrix.matrixHeight - 1 },
    ]
  }

  // Limit number of tries
  coordsToTry = coordsToTry.slice(0, maxTries)

  for (const { x, y } of coordsToTry) {
    const url = resLink
      .replace(/\{z\}/gi, String(z))
      .replace(/\{x\}/gi, String(x))
      .replace(/\{y\}/gi, String(y))
    triedUrls.push(url)
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 5000)
      const resp = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
      })
      clearTimeout(timeout)
      if (resp.ok && resp.status === 200) {
        return { isValid: true, status: resp.status, triedUrls }
      }
      // Accept 204, 401, 403 as "maybe valid"
      if (ACCEPTABLE_WMTS_TILE_STATUSES.includes(resp.status)) {
        return { isValid: false, status: resp.status, triedUrls }
      }
    } catch {
      // Ignore fetch errors, try next
    }
  }
  return { isValid: false, triedUrls }
}
